import React, { useState, useEffect } from 'react';
import { Moon, Sun, Menu, X } from 'lucide-react';
import { HashLink } from 'react-router-hash-link';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n/LanguageContext';

export default function Header({ darkMode = false, toggleDarkMode = () => {} }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1024);
  const [isSmall, setIsSmall]     = useState(() => window.innerWidth < 480);
  const { t, lang, setLang } = useTranslation();

  useEffect(() => {
    const handler = () => {
      setScrolled(window.scrollY > 12);
      setIsDesktop(window.innerWidth >= 1024);
      setIsSmall(window.innerWidth < 480);
    };
    window.addEventListener('scroll', handler, { passive: true });
    window.addEventListener('resize', handler, { passive: true });
    return () => {
      window.removeEventListener('scroll', handler);
      window.removeEventListener('resize', handler);
    };
  }, []);

  // Close mobile menu when switching to desktop
  useEffect(() => { if (isDesktop) setMobileOpen(false); }, [isDesktop]);

  const bg     = darkMode
    ? scrolled ? 'rgba(10,10,15,0.90)' : 'rgba(10,10,15,0.60)'
    : scrolled ? 'rgba(248,250,255,0.92)' : 'rgba(248,250,255,0.70)';
  const border = scrolled
    ? darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'
    : 'transparent';
  const text         = darkMode ? 'rgba(255,255,255,0.88)' : 'rgba(15,15,26,0.88)';
  const muted        = darkMode ? 'rgba(255,255,255,0.45)' : 'rgba(15,15,26,0.42)';
  const subtleBorder = darkMode ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.08)';
  const accent       = '#6366f1';
  const accent2      = '#8b5cf6';

  const navLinks = [
    { label: t('nav.services'), to: '/#leistungen', hash: true },
    { label: t('nav.whyUs'),    to: '/#warum-wir',  hash: true },
    { label: 'Shop',            to: '/shop',         hash: false },
  ];

  const NavLink = ({ link, mobile = false, onClick }) => {
    const [hov, setHov] = useState(false);
    const style = mobile ? {
      display: 'block', padding: '11px 14px', borderRadius: 10, textDecoration: 'none',
      fontSize: 14, fontWeight: 500, color: text,
      background: hov ? (darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)') : 'transparent',
      transition: 'background 0.2s',
    } : {
      fontSize: 14, fontWeight: 500, textDecoration: 'none',
      color: hov ? text : muted,
      transition: 'color 0.2s',
    };
    const props = { style, onClick, onMouseEnter: () => setHov(true), onMouseLeave: () => setHov(false) };
    return link.hash
      ? <HashLink smooth to={link.to} {...props}>{link.label}</HashLink>
      : <Link to={link.to} {...props}>{link.label}</Link>;
  };

  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 50,
      background: bg,
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderBottom: `1px solid ${border}`,
      transition: 'background 0.3s, border-color 0.3s',
    }}>
      <div style={{
        maxWidth: 1280, margin: '0 auto',
        padding: `0 ${isSmall ? 12 : 24}px`,
        height: 62, display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', gap: isSmall ? 8 : 16,
      }}>

        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: isSmall ? 6 : 9, textDecoration: 'none', flexShrink: 0 }}>
          <img src="/DDLogoNoBG.png" alt="DerrDigital" style={{ width: isSmall ? 28 : 36, height: isSmall ? 28 : 36 }} />
          <span style={{
            fontWeight: 800, fontSize: isSmall ? 14 : 17, letterSpacing: '-0.02em',
            background: `linear-gradient(135deg, ${accent}, ${accent2})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            DerrDigital
          </span>
        </Link>

        {/* Desktop nav — only rendered when isDesktop */}
        {isDesktop && (
          <nav style={{ display: 'flex', alignItems: 'center', gap: 28, flex: 1, justifyContent: 'center' }}>
            {navLinks.map(l => <NavLink key={l.to} link={l} />)}
            <Link to="/preview" style={{
              fontSize: 13, fontWeight: 700, textDecoration: 'none',
              color: accent, background: accent + '12', border: `1px solid ${accent}30`,
              padding: '4px 12px', borderRadius: 999, transition: 'background 0.2s, box-shadow 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = accent + '22'; e.currentTarget.style.boxShadow = `0 0 0 2px ${accent}25`; }}
              onMouseLeave={e => { e.currentTarget.style.background = accent + '12'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              Dein Design
            </Link>
          </nav>
        )}

        {/* Right actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: isSmall ? 4 : 8, flexShrink: 0 }}>

          {/* Language switcher */}
          <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${subtleBorder}` }}>
            {['de', 'en'].map(l => (
              <button key={l} onClick={() => setLang(l)} style={{
                padding: isSmall ? '5px 7px' : '5px 9px', fontSize: 11, fontWeight: 700, border: 'none', cursor: 'pointer',
                background: lang === l ? accent : 'transparent',
                color: lang === l ? '#fff' : muted,
                textTransform: 'uppercase', letterSpacing: '0.06em',
                transition: 'background 0.2s, color 0.2s',
              }}>
                {l}
              </button>
            ))}
          </div>

          {/* Dark mode toggle */}
          <IconBtn onClick={toggleDarkMode} border={subtleBorder} muted={muted} text={text} accent={accent}>
            {darkMode ? <Sun size={14} /> : <Moon size={14} />}
          </IconBtn>

          {/* CTA — desktop only */}
          {isDesktop && (
            <Link to="/kontakt" style={{
              display: 'flex', alignItems: 'center', padding: '8px 18px', borderRadius: 10,
              background: `linear-gradient(135deg, ${accent}, ${accent2})`,
              color: '#fff', fontWeight: 700, fontSize: 13, textDecoration: 'none',
              boxShadow: `0 4px 14px ${accent}35`, transition: 'transform 0.2s, box-shadow 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.04)'; e.currentTarget.style.boxShadow = `0 6px 20px ${accent}50`; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)';   e.currentTarget.style.boxShadow = `0 4px 14px ${accent}35`; }}
            >
              {t('nav.contact')}
            </Link>
          )}

          {/* Burger — mobile only */}
          {!isDesktop && (
            <IconBtn onClick={() => setMobileOpen(!mobileOpen)} border={subtleBorder} muted={muted} text={text} accent={accent}>
              {mobileOpen ? <X size={15} /> : <Menu size={15} />}
            </IconBtn>
          )}
        </div>
      </div>

      {/* Mobile menu */}
      {!isDesktop && (
        <div style={{
          maxHeight: mobileOpen ? 400 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.35s cubic-bezier(0.16,1,0.3,1)',
        }}>
          <div style={{
            margin: '0 12px 12px',
            background: darkMode ? 'rgba(12,12,20,0.98)' : 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${subtleBorder}`,
            borderRadius: 14,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            overflow: 'hidden',
          }}>
            <nav style={{ padding: 8 }}>
              {navLinks.map(l => (
                <NavLink key={l.to} link={l} mobile onClick={() => setMobileOpen(false)} />
              ))}
              <NavLink link={{ label: 'Dein Design', to: '/preview', hash: false }} mobile onClick={() => setMobileOpen(false)} />
              <div style={{ padding: '8px 0 2px', borderTop: `1px solid ${subtleBorder}`, marginTop: 6 }}>
                <Link to="/kontakt" onClick={() => setMobileOpen(false)} style={{
                  display: 'block', padding: '11px 14px', borderRadius: 10,
                  textAlign: 'center', textDecoration: 'none',
                  background: `linear-gradient(135deg, ${accent}, ${accent2})`,
                  color: '#fff', fontWeight: 700, fontSize: 14,
                }}>
                  {t('nav.contact')}
                </Link>
              </div>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}

function IconBtn({ onClick, border, muted, text, accent, children }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} style={{
      width: 34, height: 34, borderRadius: 8,
      border: `1px solid ${hov ? accent + '55' : border}`,
      background: 'transparent', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: hov ? text : muted,
      transition: 'color 0.2s, border-color 0.2s',
    }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      {children}
    </button>
  );
}
